import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'

import { Button } from '../../../components/Button'
import { useAuth } from '../../AuthContext'
import {
  ONLINE_SERVICES,
  SERVICE_NAMES,
  TRANSPORT_CONFIG_KEY,
  fetchCapacity,
  removeDatedCapacity,
  setDatedCapacity,
  setDefaultCapacity,
  type OnlineService,
} from '../../api/transportConfig'
import { SavedNote } from './SavedNote'
import { SectionState } from './SectionState'
import { errorMessage, retryUnlessForbidden } from './errorMessage'
import { INPUT, LABEL } from './styles'
import { useSyncedText } from './useSyncedText'

const LIMIT = /^\d+$/
/** The server's ceiling (T06), checked here so the page can say it in words. */
const MAX_DAILY = 500

/**
 * Daily capacity per service (owner Q3): a default for every day, and dated
 * overrides such as a public holiday. An empty default means CLOSED — never
 * unlimited — so a day with no limit set cannot sell a trip (ADR-0066).
 */
export function CapacitySection(): ReactNode {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'capacity'],
    queryFn: () => fetchCapacity(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })
  const state = (
    <SectionState
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      what="capacity"
    />
  )
  if (!query.data) return state
  const { defaults, overrides } = query.data

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-ink-3">
        How many bookings each service takes per day. Leave a daily limit empty
        to close the service.
      </p>
      <ul className="flex flex-col divide-y divide-line-1 rounded-[12px] border border-line-1">
        {ONLINE_SERVICES.map((service) => (
          <DefaultRow
            key={service}
            service={service}
            limit={defaults[service]}
          />
        ))}
        <li className="flex flex-wrap items-center gap-4 px-4 py-3 text-sm">
          <span className="min-w-0 flex-1 font-medium text-ink-1">
            {SERVICE_NAMES.executive}
          </span>
          <span className="text-ink-3">Booked by phone</span>
        </li>
      </ul>

      <section
        aria-labelledby="overrides-heading"
        className="flex flex-col gap-3"
      >
        <h2 id="overrides-heading" className="text-sm font-semibold text-ink-1">
          Dates with a different limit
        </h2>
        {overrides.length === 0 ? (
          <p className="text-sm text-ink-2">
            No dates set. Every day uses the daily limit.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-line-1 rounded-[12px] border border-line-1">
            {overrides.map((o) => (
              <OverrideRow
                key={`${o.service}:${o.date}`}
                service={o.service}
                date={o.date}
                limit={o.max_bookings}
              />
            ))}
          </ul>
        )}
        <AddOverride />
      </section>
    </div>
  )
}

function useSaved() {
  const queryClient = useQueryClient()
  return () =>
    void queryClient.invalidateQueries({ queryKey: TRANSPORT_CONFIG_KEY })
}

function DefaultRow({
  service,
  limit,
}: {
  service: OnlineService
  limit: number | null
}): ReactNode {
  const { token } = useAuth()
  const refresh = useSaved()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useSyncedText(limit === null ? '' : String(limit))
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: (value: number | null) =>
      setDefaultCapacity(token, service, value),
    onSuccess: () => {
      setProblem(null)
      setSaved(true)
      input.current?.focus()
      refresh()
    },
    onError: (e) => setProblem(errorMessage(e)),
  })
  const trimmed = text.trim()
  const dirty = trimmed !== (limit === null ? '' : String(limit))

  function submit(e: FormEvent): void {
    e.preventDefault()
    if (!dirty) return
    if (
      trimmed !== '' &&
      (!LIMIT.test(trimmed) || Number(trimmed) > MAX_DAILY)
    ) {
      setProblem(
        `A daily limit is a whole number up to ${MAX_DAILY}, or empty to close the service.`,
      )
      return
    }
    save.mutate(trimmed === '' ? null : Number(trimmed))
  }

  const id = `capacity-${service}`
  const stateId = `${id}-state`
  const name = SERVICE_NAMES[service]
  return (
    <li className="px-4 py-3 text-sm">
      <form
        onSubmit={submit}
        className="flex flex-wrap items-center gap-x-4 gap-y-2"
      >
        <span className="min-w-0 flex-1">
          {/* The label is the name alone; the current state describes the
              field, so the field's name does not change with its value. */}
          <label htmlFor={id} className="font-medium text-ink-1">
            {name}
          </label>
          <span id={stateId} className="block text-xs text-ink-3">
            {limit === null ? 'Closed' : `${limit} bookings a day`}
          </span>
        </span>
        <input
          ref={input}
          id={id}
          className={`${INPUT} w-24`}
          inputMode="numeric"
          value={text}
          placeholder="Closed"
          aria-describedby={stateId}
          onChange={(e) => {
            setText(e.target.value)
            setProblem(null)
            setSaved(false)
          }}
        />
        {dirty && (
          <Button
            kind="secondary"
            type="submit"
            loading={save.isPending}
            aria-label={`Save daily limit for ${name}`}
          >
            Save
          </Button>
        )}
        <SavedNote show={saved && !dirty} />
        {problem && (
          <span role="alert" className="w-full text-xs text-bed-bad">
            {problem}
          </span>
        )}
      </form>
    </li>
  )
}

function OverrideRow({
  service,
  date,
  limit,
}: {
  service: OnlineService
  date: string
  limit: number
}): ReactNode {
  const { token } = useAuth()
  const refresh = useSaved()
  const [problem, setProblem] = useState<string | null>(null)
  const remove = useMutation({
    mutationFn: () => removeDatedCapacity(token, service, date),
    onSuccess: refresh,
    onError: (e) => setProblem(errorMessage(e)),
  })
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
      <span className="min-w-0 flex-1 text-ink-1">
        {SERVICE_NAMES[service]} · {formatDay(date)}
      </span>
      <span className="text-ink-2">
        {limit === 0 ? 'Closed' : `${limit} bookings`}
      </span>
      <Button
        kind="ghost"
        onClick={() => remove.mutate()}
        loading={remove.isPending}
        aria-label={`Remove ${SERVICE_NAMES[service]} on ${formatDay(date)}`}
      >
        Remove
      </Button>
      {problem && (
        <span role="alert" className="w-full text-xs text-bed-bad">
          {problem}
        </span>
      )}
    </li>
  )
}

function AddOverride(): ReactNode {
  const { token } = useAuth()
  const refresh = useSaved()
  const [service, setService] = useState<OnlineService>('standard_ride')
  const [date, setDate] = useState('')
  const [limit, setLimit] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const add = useMutation({
    mutationFn: () =>
      setDatedCapacity(token, service, date, Number(limit.trim())),
    onSuccess: () => {
      setDate('')
      setLimit('')
      setProblem(null)
      refresh()
    },
    onError: (e) => setProblem(errorMessage(e)),
  })

  function submit(e: FormEvent): void {
    e.preventDefault()
    if (date === '') {
      setProblem('Choose a date.')
      return
    }
    if (!LIMIT.test(limit.trim()) || Number(limit.trim()) > MAX_DAILY) {
      setProblem(
        `The limit for that day is a whole number up to ${MAX_DAILY}; 0 closes it.`,
      )
      return
    }
    add.mutate()
  }

  return (
    <form
      onSubmit={submit}
      aria-label="Set a limit for one date"
      className="flex flex-col gap-3 rounded-[12px] border border-line-1 p-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <label className={LABEL}>
          Service
          <select
            className={INPUT}
            value={service}
            onChange={(e) => setService(e.target.value as OnlineService)}
          >
            {ONLINE_SERVICES.map((s) => (
              <option key={s} value={s}>
                {SERVICE_NAMES[s]}
              </option>
            ))}
          </select>
        </label>
        <label className={LABEL}>
          Date
          <input
            type="date"
            className={INPUT}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <label className={LABEL}>
          Limit that day
          <input
            className={INPUT}
            inputMode="numeric"
            value={limit}
            placeholder="0 closes the day"
            onChange={(e) => setLimit(e.target.value)}
          />
        </label>
      </div>
      {problem && (
        <p role="alert" className="text-sm text-bed-bad">
          {problem}
        </p>
      )}
      <span>
        <Button type="submit" loading={add.isPending}>
          Set limit for that date
        </Button>
      </span>
    </form>
  )
}

function formatDay(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
